import { Methods as JobListAPI } from '@doda/api-client/aspida/api/v1/jobs';
import {
  AppQueryParams,
  QueryOtherParams,
} from '@doda/common/src/constants/queryParams';
import { cassifyListAsLMS } from '@doda/common/src/utils/convArrayUtil';
import { Modify } from '@doda/common/src/utils/typeUtil';
import {
  ACCETABLE_ESTABLISH_VALUES,
  ACCETABLE_EMPLOY_NUM_VALUES,
  ACCETABLE_LOWER_LIMIT_INCOME_VALUES,
  ACCETABLE_UPPER_LIMIT_INCOME_VALUES,
  ACCETABLE_JOB_OFFER_KIND_VALUES,
  ACCETABLE_SORT_VALUES,
  ACCETABLE_INCLUDE_KEYWORD_CATEGORY_VALUES,
  ACCETABLE_EXCLUDE_KEYWORD_CATEGORY_VALUES,
} from '@doda/common/src/constants/accetableRequestValues';
import { toNewQueryValue } from '@doda/common/src/logics/createJobSearchRequestParams';
import { getIsOnePrefecture } from '@doda/common/src/logics/onePrefectureSearch/getIsOnePrefecture';
import { opToJobQuery } from '@doda/common/src/utils/optionQueryUtil';

const DISPLAY_NUMBER = 50;

export type JobListQuery = NonNullable<JobListAPI['get']['query']>;

/**
 * 検索結果一覧APIリクエストパラメータを生成する関数
 *
 * @param {AppQueryParams} query - アプリケーションのクエリパラメータ
 * @param {QueryOtherParams} [queryother] - その他のクエリパラメータ（オプション）
 * @returns {JobListQuery} - 生成されたリクエストパラメータ
 */
export const createSearchConditionRequestParams = (
  query: AppQueryParams,
  queryother?: QueryOtherParams
) => {
  // 職種を大・中・小分類に分ける
  const {
    lList: searchJobTypeLId,
    mList: searchJobTypeMId,
    sList: searchJobTypeSId,
  } = cassifyListAsLMS(query.oc);

  // 業種を大・小分類に分ける
  const { lList: industryLId, sList: industrySId } = cassifyListAsLMS(
    query.ind
  );

  const numOfDisplay =
    query.so !== undefined && [10, 20, 50].includes(Number(query.so))
      ? Number(query.so)
      : DISPLAY_NUMBER;
  const requestParams: Modify<JobListQuery, { offset: number }> = {
    offset: ((Number(query.page) || 1) - 1) * numOfDisplay,
  };
  if (query.ar) requestParams.areaId = query.ar;
  if (query.pr) requestParams.prefectureId = query.pr;
  if (query.ci) requestParams.cityId = query.ci;
  if (query.wa) requestParams.wardId = query.wa;
  if (query.rt) requestParams.routeId = query.rt;
  if (query.st) requestParams.stationId = query.st;
  if (query.oc) {
    requestParams.searchJobTypeLId = searchJobTypeLId;
    requestParams.searchJobTypeMId = searchJobTypeMId;
    requestParams.searchJobTypeSId = searchJobTypeSId;
  }
  if (query.ind) {
    requestParams.industryLId = industryLId;
    requestParams.industrySId = industrySId;
  }
  if (query.qc) requestParams.qualificationId = query.qc;
  if (query.k) {
    const includedKeyword = getKeyword(query.k);
    if (includedKeyword) {
      requestParams.includedKeyword = includedKeyword;
      requestParams.includedKeywordCategory = getIncludedKeywordCategory(
        query.kwc
      );
    }
  }
  if (query.feak) {
    const includedKeyword = getKeyword(query.feak);
    if (includedKeyword) {
      requestParams.includedKeyword = includedKeyword;
      requestParams.includedKeywordCategory = getIncludedKeywordCategory(
        query.feakwc
      );
    }
  }
  if (query.ni) {
    const excludedKeyword = getKeyword(query.ni);
    if (excludedKeyword) {
      requestParams.excludedKeyword = excludedKeyword;
      requestParams.excludedKeywordCategory = getExcludedKeywordCategory(
        query.niwc
      );
    }
  }
  if (query.feani) {
    const excludedKeyword = getKeyword(query.feani);
    if (excludedKeyword) {
      requestParams.excludedKeyword = excludedKeyword;
      requestParams.excludedKeywordCategory = getExcludedKeywordCategory(
        query.feaniwc
      );
    }
  }
  if (query.op) {
    Object.entries(opToJobQuery(query.op)).forEach(([key, value]) => {
      requestParams[key] = value;
    });
  }
  if (query.es)
    requestParams.establish = toNewQueryValue(
      query.es,
      ACCETABLE_ESTABLISH_VALUES
    ) as JobListQuery['establish'];
  if (query.ne)
    requestParams.employNum = toNewQueryValue(
      query.ne,
      ACCETABLE_EMPLOY_NUM_VALUES
    ) as JobListQuery['employNum'];
  if (query.ha) {
    requestParams.lowerLimitIncome = toNewQueryValue(
      query.ha[0],
      ACCETABLE_LOWER_LIMIT_INCOME_VALUES
    ) as JobListQuery['lowerLimitIncome'];
    requestParams.upperLimitIncome = toNewQueryValue(
      query.ha[1],
      ACCETABLE_UPPER_LIMIT_INCOME_VALUES
    ) as JobListQuery['upperLimitIncome'];
  }
  if (query.tp)
    requestParams.jobOfferKind = toNewQueryValue(
      query.tp,
      ACCETABLE_JOB_OFFER_KIND_VALUES
    ) as JobListQuery['jobOfferKind'];
  if (query.ds) {
    requestParams.sort = ACCETABLE_SORT_VALUES[query.ds];
  } else if (queryother && queryother.slashSortflg) requestParams.sort = '4';
  if (query.pf)
    requestParams.isOnePrefecture =
      query.pf === '1' && getIsOnePrefecture(query); // 都道府県のみフラグが1でかつ、他のエリアを検索していない
  if (query.so)
    requestParams.limit = [10, 20, 50].includes(Number(query.so))
      ? Number(query.so)
      : undefined;
  return requestParams;
};

/**
 * kからIncludedKeyword、niからexcludedKeywordを生成する関数
 *
 * @param {unknown} k - クエリパラメータから取得したキーワード
 * @returns {string[] | undefined} - 変換されたキーワードまたは未定義
 */
export const getKeyword = (k: unknown): string[] | undefined => {
  if (typeof k !== 'string') return undefined;
  const trimmedText = k.trim();
  const replacedKeyword = trimmedText.split(/\s+/g);
  return replacedKeyword;
};

/**
 * kwcからincludedKeywordCategoryを生成する関数
 *
 * @param {unknown} kwc - クエリパラメータから取得したキーワードカテゴリ
 * @returns {JobListQuery['includedKeywordCategory']} - 変換されたキーワードカテゴリ
 */
export const getIncludedKeywordCategory = (kwc: unknown) => {
  if (typeof kwc !== 'string' || !kwc) return '1';
  const includedKeywordCategory = toNewQueryValue(
    kwc,
    ACCETABLE_INCLUDE_KEYWORD_CATEGORY_VALUES
  ) as JobListQuery['includedKeywordCategory'];
  return includedKeywordCategory;
};

/**
 * niwcからexcludedKeywordCategoryを生成する関数
 *
 * @param {unknown} niwc - クエリパラメータから取得した除外キーワードカテゴリ
 * @returns {JobListQuery['excludedKeywordCategory']} - 変換された除外キーワードカテゴリ
 */
export const getExcludedKeywordCategory = (niwc: unknown) => {
  if (typeof niwc !== 'string' || !niwc) return '1';
  const excludedKeywordCategory = toNewQueryValue(
    niwc,
    ACCETABLE_EXCLUDE_KEYWORD_CATEGORY_VALUES
  ) as JobListQuery['excludedKeywordCategory'];
  return excludedKeywordCategory;
};
